/**
 * Stat Icon Utilities
 * 
 * Provides consistent emoji icons for all stat types across the application.
 * This ensures all apps (Score, Reports, Replay) use the same icons.
 */

/**
 * Get emoji icon for a stat type based on stat_key
 * @param statKey - The stat_key from stat_types table
 * @returns Emoji string representing the stat type
 */
export const getStatIcon = (statKey: string | null | undefined): string => {
  if (!statKey) return '📝';

  // Normalize the stat key to lowercase for case-insensitive matching
  const normalizedKey = statKey.toLowerCase();

  // Goals & Scoring
  if (normalizedKey.includes('goal') || normalizedKey === 'goal') {
    if (normalizedKey.includes('own')) return '🥅';
    return '⚽';
  }

  // Penalties
  if (normalizedKey.includes('penalty')) {
    if (normalizedKey.includes('scored') || normalizedKey.includes('shootout_scored')) return '🥅';
    if (normalizedKey.includes('missed') || normalizedKey.includes('shootout_missed')) return '❌';
    if (normalizedKey.includes('punch')) return '👊';
    if (normalizedKey.includes('claim')) return '✋';
    return '🥅';
  }

  // Cards
  if (normalizedKey.includes('yellow_card')) return '🟨';
  if (normalizedKey.includes('red_card')) return '🟥';

  // Substitutions
  if (normalizedKey.includes('substitution')) return '🔄';

  // Goalkeeper Actions
  if (normalizedKey.includes('save')) return '🛡️';
  if (normalizedKey.includes('claim')) return '✋';
  if (normalizedKey.includes('punch')) return '👊';
  if (normalizedKey.includes('gk_interception')) return '🛡️';

  // Shots - Inside Box
  if (normalizedKey.includes('shot_inside_box')) {
    if (normalizedKey.includes('on_target')) return '🎯';
    if (normalizedKey.includes('off_target')) return '📉';
    if (normalizedKey.includes('blocked')) return '🚫';
    if (normalizedKey.includes('side_post')) return '📌';
    if (normalizedKey.includes('crossbar')) return '📊';
    return '🎯';
  }

  // Shots - Outside Box
  if (normalizedKey.includes('shot_outside_box')) {
    if (normalizedKey.includes('on_target')) return '🎯';
    if (normalizedKey.includes('off_target')) return '📉';
    if (normalizedKey.includes('blocked')) return '🚫';
    if (normalizedKey.includes('side_post')) return '📌';
    if (normalizedKey.includes('crossbar')) return '📊';
    return '🎯';
  }

  // Distribution - Passes (check backward first for specificity)
  if (normalizedKey.includes('pass')) {
    if (normalizedKey.includes('backward')) {
      if (normalizedKey.includes('short')) {
        if (normalizedKey.includes('success')) return '↩️';
        if (normalizedKey.includes('failure')) return '↶';
      }
      if (normalizedKey.includes('long')) {
        if (normalizedKey.includes('success')) return '↪️';
        if (normalizedKey.includes('failure')) return '↷';
      }
    }
    if (normalizedKey.includes('short')) {
      if (normalizedKey.includes('success')) return '✅';
      if (normalizedKey.includes('failure')) return '❌';
      return '✅';
    }
    if (normalizedKey.includes('long')) {
      if (normalizedKey.includes('success')) return '📤';
      if (normalizedKey.includes('failure')) return '📥';
      return '📤';
    }
    return '✅';
  }

  // Crosses (check backward first for specificity)
  if (normalizedKey.includes('cross')) {
    if (normalizedKey.includes('backward')) {
      if (normalizedKey.includes('success')) return '🔄';
      if (normalizedKey.includes('failure')) return '🔁';
    }
    if (normalizedKey.includes('success')) return '✈️';
    if (normalizedKey.includes('failure')) return '💨';
    return '✈️';
  }

  // Defense
  if (normalizedKey.includes('tackle')) {
    if (normalizedKey.includes('success')) return '🛡️';
    if (normalizedKey.includes('failure')) return '⚔️';
    return '🛡️';
  }
  if (normalizedKey.includes('clearance')) return '🧹';
  if (normalizedKey.includes('interception') && !normalizedKey.includes('gk_')) return '🪝';

  // Duels
  if (normalizedKey.includes('dribble')) {
    if (normalizedKey.includes('won')) return '🏃';
    return '🏃';
  }
  if (normalizedKey.includes('aerial')) {
    if (normalizedKey.includes('won')) return '🦅';
    return '🦅';
  }

  // Freekicks
  if (normalizedKey.includes('direct_fk')) return '🎯';
  if (normalizedKey.includes('indirect_fk')) return '🎲';

  // Outside Play
  if (normalizedKey.includes('throw_in')) return '📨';
  if (normalizedKey.includes('corner')) return '📐';

  // Errors
  if (normalizedKey.includes('offside')) return '🚫';
  if (normalizedKey.includes('handball')) return '✋';
  if (normalizedKey.includes('foul')) {
    if (normalizedKey.includes('own_half')) return '⚠️';
    if (normalizedKey.includes('opp_half')) return '⚠️';
    return '⚠️';
  }

  // Game Flow
  if (normalizedKey.includes('kick_off')) return '🚀';
  if (normalizedKey.includes('end_1st_half')) return '⏸️';
  if (normalizedKey.includes('start_2nd_half')) return '▶️';
  if (normalizedKey.includes('start_extra_time_1st_half')) return '⏩';
  if (normalizedKey.includes('end_extra_time_1st_half')) return '⏸️';
  if (normalizedKey.includes('start_extra_time_2nd_half')) return '▶️';
  if (normalizedKey.includes('penalty_shootout')) return '🥅';
  if (normalizedKey.includes('full_time')) return '🏁';

  // Default fallback
  return '📝';
};

/**
 * Get color class for a stat type based on stat_key
 * @param statKey - The stat_key from stat_types table
 * @returns Tailwind CSS color class
 */
export const getStatColor = (statKey: string | null | undefined): string => {
  if (!statKey) return 'bg-blue-500';

  const normalizedKey = statKey.toLowerCase();

  // Goal events - green
  if (normalizedKey.includes('goal')) return 'bg-green-500';
  
  // Card events - yellow/red
  if (normalizedKey.includes('yellow_card')) return 'bg-yellow-500';
  if (normalizedKey.includes('red_card')) return 'bg-red-500';
  
  // Substitution - purple
  if (normalizedKey.includes('substitution')) return 'bg-purple-500';
  
  // Goalkeeper actions - blue
  if (normalizedKey.includes('save') || normalizedKey.includes('claim') || normalizedKey.includes('punch')) {
    return 'bg-blue-500';
  }
  
  // Shots on target - orange
  if (normalizedKey.includes('shot') && normalizedKey.includes('on_target')) {
    return 'bg-orange-500';
  }
  
  // Shots off target - gray
  if (normalizedKey.includes('shot') && normalizedKey.includes('off_target')) {
    return 'bg-gray-500';
  }
  
  // Successful passes - green
  if (normalizedKey.includes('pass') && normalizedKey.includes('success')) {
    return 'bg-green-500';
  }
  
  // Failed passes - red
  if (normalizedKey.includes('pass') && normalizedKey.includes('failure')) {
    return 'bg-red-500';
  }
  
  // Defense - indigo
  if (normalizedKey.includes('tackle') || normalizedKey.includes('clearance') || normalizedKey.includes('interception')) {
    return 'bg-indigo-500';
  }
  
  // Default - blue
  return 'bg-blue-500';
};

/**
 * Get color classes for event badges/cards (text, background, border)
 * @param statKey - The stat_key from stat_types table
 * @returns Object with text, background, and border color classes
 */
export const getStatColorClasses = (statKey: string | null | undefined): {
  text: string;
  bg: string;
  border: string;
} => {
  if (!statKey) {
    return {
      text: 'text-blue-600',
      bg: 'bg-blue-50',
      border: 'border-blue-200'
    };
  }

  const normalizedKey = statKey.toLowerCase();

  // Goal events
  if (normalizedKey.includes('goal') || normalizedKey === 'goal') {
    return {
      text: 'text-green-600',
      bg: 'bg-green-50',
      border: 'border-green-200'
    };
  }

  // Penalties
  if (normalizedKey.includes('penalty_scored')) {
    return {
      text: 'text-green-600',
      bg: 'bg-green-50',
      border: 'border-green-200'
    };
  }
  if (normalizedKey.includes('penalty_missed')) {
    return {
      text: 'text-red-600',
      bg: 'bg-red-50',
      border: 'border-red-200'
    };
  }

  // Cards
  if (normalizedKey.includes('yellow_card')) {
    return {
      text: 'text-yellow-700',
      bg: 'bg-yellow-50',
      border: 'border-yellow-200'
    };
  }
  if (normalizedKey.includes('red_card')) {
    return {
      text: 'text-red-700',
      bg: 'bg-red-50',
      border: 'border-red-200'
    };
  }

  // Substitution
  if (normalizedKey.includes('substitution')) {
    return {
      text: 'text-purple-600',
      bg: 'bg-purple-50',
      border: 'border-purple-200'
    };
  }

  // Goalkeeper
  if (normalizedKey.includes('save') || normalizedKey.includes('claim') || normalizedKey.includes('punch')) {
    return {
      text: 'text-blue-600',
      bg: 'bg-blue-50',
      border: 'border-blue-200'
    };
  }

  // Shots
  if (normalizedKey.includes('shot')) {
    if (normalizedKey.includes('on_target')) {
      return {
        text: 'text-orange-600',
        bg: 'bg-orange-50',
        border: 'border-orange-200'
      };
    }
    if (normalizedKey.includes('off_target')) {
      return {
        text: 'text-gray-600',
        bg: 'bg-gray-50',
        border: 'border-gray-200'
      };
    }
  }

  // Passes
  if (normalizedKey.includes('pass')) {
    if (normalizedKey.includes('success')) {
      return {
        text: 'text-green-600',
        bg: 'bg-green-50',
        border: 'border-green-200'
      };
    }
    if (normalizedKey.includes('failure')) {
      return {
        text: 'text-red-600',
        bg: 'bg-red-50',
        border: 'border-red-200'
      };
    }
  }

  // Crosses
  if (normalizedKey.includes('cross')) {
    if (normalizedKey.includes('success')) {
      return {
        text: 'text-blue-600',
        bg: 'bg-blue-50',
        border: 'border-blue-200'
      };
    }
    if (normalizedKey.includes('failure')) {
      return {
        text: 'text-red-600',
        bg: 'bg-red-50',
        border: 'border-red-200'
      };
    }
  }

  // Defense
  if (normalizedKey.includes('tackle') || normalizedKey.includes('clearance') || normalizedKey.includes('interception')) {
    return {
      text: 'text-indigo-600',
      bg: 'bg-indigo-50',
      border: 'border-indigo-200'
    };
  }

  // Default
  return {
    text: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200'
  };
};

